import React, { useContext } from 'react'
import {
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TableFooter,
  TablePagination,
  Divider,
  TextField,
} from '@material-ui/core'
import { BrokerPortalContextStore } from '../../../context/BrokerPortalContext'
import TablePaginationActions from '@material-ui/core/TablePagination/TablePaginationActions'
import FlexView from 'react-flexview/lib'
import NewFillButton from '../../Core/NewFillButton'
import { billingPeriodPretty, liabilityLimitPretty } from '../../../services/utils'
import QuoteModel from '../../../models/insurance-models/QuoteModel'
import { FirebaseService, FirebaseConfigKey } from '../../../services/FirebaseService'
import dayjs from 'dayjs'
import SimpleAccordion from '../../Core/Expansion/SimpleAccordion'
import { TextButton } from '../../Core/Buttons/TextButton'
import { EmptyMessageTable } from './EmptyTableMessage'
import { RecentSubmissionTableRow } from './RecentSubmissionTableRow'
import { isMobile } from 'react-device-detect'
import { SessionContextStore } from '../../../context/SessionContext'
import { useHistory } from 'react-router-dom'
import { SortableTableCell } from '../../Core/Table/EnhancedTableHead'
import { GlobalTextField } from '../../Core/Input/ControlledTextField'
import { activeAccountsStatus } from '../../../Constants'

export const RecentSubmissionsTable = ({ onRowClick, newCustomerClick, onManagePolicyClick }) => {
  const brokerPortalContext = useContext(BrokerPortalContextStore)
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(8)
  const [textFilter, setTextFilter] = React.useState('')
  const [recentSubmissionsLoaded, setRecentSubmissionsLoaded] = React.useState(false)
  const [sortId, setSortId] = React.useState('')
  const [sortOrder, setSortOrder] = React.useState('')
  const [results, setResults] = React.useState([])

  React.useEffect(() => {
    if (!brokerPortalContext.loadingUsers) {
      setRecentSubmissionsLoaded(true)
    }
  }, [brokerPortalContext.loadingUsers])

  React.useEffect(() => {
    applyFilterAndSort()
  }, [brokerPortalContext.quotes, brokerPortalContext.activeUsers, sortId, sortOrder, textFilter])

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangePage = (event, newPage) => setPage(newPage)
  const onQuoteClick = user => onRowClick && onRowClick(user)
  const handleSortRequest = (id, order) => setSortId(id) || setSortOrder(order)
  const onSearchContentChange = event => event && event.target && (setTextFilter(event.target.value.toLowerCase()) || setPage(0))

  const applyFilterAndSort = () => {
    const allUsers = brokerPortalContext.quotes
      .concat(brokerPortalContext.activeUsers)
      .filter(i => !activeAccountsStatus.includes(i.status))
    let filtered = allUsers.filter(
      (quote: QuoteModel) => quote.email.includes(textFilter) || `${quote.firstName} ${quote.lastName}`.toLowerCase().includes(textFilter)
    )

    if (sortId != '') {
      filtered = filtered.sort((a, b) => {
        if (a[sortId] < b[sortId]) return -1
        else return 1
      })

      if (sortOrder == 'asc') filtered = filtered.reverse()
    }
    setResults(filtered)
  }

  return (
    <>
      <FlexView vAlignContent={'center'} className="space-between">
        <FlexView vAlignContent={'center'} className="full-width margin-right-basic">
          <div className="text-title blue bold">Recent Submissions</div>
          {!isMobile && (
            <GlobalTextField
              fullWidth
              label="Search"
              name="search_accounts"
              className="margin-left-basic"
              onChange={onSearchContentChange}
            />
          )}
        </FlexView>
        <FlexView className="margin-right-basic">
          <NewFillButton onClick={newCustomerClick}>New Customer</NewFillButton>
        </FlexView>
      </FlexView>
      {isMobile && (
        <GlobalTextField fullWidth label="Search" name="search_accounts" className="margin-top-6" onChange={onSearchContentChange} />
      )}
      <Divider />
      {recentSubmissionsLoaded && brokerPortalContext.quotes.length == 0 ? (
        <EmptyMessageTable newUserClick={newCustomerClick} message={"You don't have any submissions yet."} />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableTableCell handleSortRequest={handleSortRequest} id={'name'} currentOrderById={sortId} className="bolder">
                  Account Name
                </SortableTableCell>
                <SortableTableCell handleSortRequest={handleSortRequest} id={'email'} currentOrderById={sortId} className="bolder">
                  Email
                </SortableTableCell>
                <TableCell className="bolder">Last Activity</TableCell>
                <TableCell className="bolder">Status</TableCell>
                <TableCell className="bolder"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((quote: QuoteModel, index) => (
                <RecentSubmissionTableRow
                  key={index}
                  onQuoteClick={onQuoteClick}
                  quote={quote}
                  options={[
                    { onClick: () => onQuoteClick(quote), text: 'Back To Quote' },
                    { onClick: () => onManagePolicyClick(quote), text: 'Manage Policies' },
                  ]}
                />
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={3}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={results.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </>
  )
}
